import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import encodeFormData from "@splitfire-agency/raiden-library/dist/libraries/utils/encodeFormData";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import InputFile from "../../../components/Form/InputFile";
import {
  useApiError,
  withApiError,
} from "../../../components/ReactHookForm/ApiError";
import ApiErrorDetailRHF from "../../../components/ReactHookForm/ApiErrorDetailRHF";
import FormController from "../../../components/ReactHookForm/FormController";
import FormStateRHF from "../../../components/ReactHookForm/FormStateRHF";
import { AUTHORIZATIONS_AUTHORIZATION_VALUE_CUSTOMERS_DOCUMENTS_CREATE } from "../../../constants/authorizations";
import {
  CUSTOMERS_DOCUMENTS_STATES_VALUE_PENDING,
  CUSTOMERS_DOCUMENTS_STATES_VALUE_REFUSED,
} from "../../../constants/customersDocuments";
import useAuthAs from "../../../hooks/auth/as";
import useCanPerform from "../../../hooks/useCanPerform";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

const CustomersDocumentsActionCreate = withApiError(
  /**
   * @typedef {Object} Props
   * @property {number} documentId
   * @property {"customer" | "holder"} userType
   * @property {number} userId
   * @property {import("../../../types/CustomerDocument").CustomerDocument} [customerDocument]
   * @property {() => void} mutateCustomerDocument
   * @property {(props: { isAllowed: boolean, trigger: () => void }) => import("react").ReactElement | null} render
   *
   * @typedef {Props} Merged
   *
   * @param {Merged} props
   */
  function CustomersDocumentsActionCreate({
    userType,
    userId,
    documentId,
    mutateCustomerDocument,
    customerDocument,
    render,
  }) {
    const { isOpen, onOpen, onClose: _onClose } = useDisclosure();

    const canPerform = useCanPerform();

    const intl = useIntl();

    const { user } = useAuthAs();

    const form = useForm({
      defaultValues: {
        pages: [],
      },
    });
    const { handleSubmit, reset } = form;

    const { request, isLoading, toastSuccess } = useRequest();

    const { resolveApiError, resetApiErrors } = useApiError();

    const onClose = useCallback(() => {
      _onClose();
      form.reset();
    }, [_onClose, form]);

    function onSubmit(fields) {
      request(
        generateApiUri({ id: "@customers.documents.create" }),
        {
          method: "POST",
          body: encodeFormData({
            ...fields,
            data: {
              document_id: documentId,
              customer_id: userType === "customer" ? userId : undefined,
              holder_id: userType === "holder" ? userId : undefined,
            },
          }),
        },
        {
          inputMode: "formData",
        },
      )
        .then(() => {
          toastSuccess({ title: "Document téléversé avec succès" });
          mutateCustomerDocument();
          onClose();
          reset();
          resetApiErrors();
        })
        .catch((response) => {
          resolveApiError({ response, form });
        });
    }

    const formId = `customers-documents-action-upload-${documentId}`;

    return (
      <>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />

          <ModalContent>
            <FormProvider {...form}>
              <ModalHeader>
                {intl.formatMessage({
                  defaultMessage: "Téléverser des documents",
                })}
              </ModalHeader>

              <ModalCloseButton />

              <ModalBody>
                <form id={formId} onSubmit={handleSubmit(onSubmit)}>
                  <ApiErrorDetailRHF pb="1rem" />

                  <FormController
                    name="pages"
                    rules={{ required: true }}
                    renderWithFormControl={(field) => (
                      <InputFile {...field} isMultiple={true} />
                    )}
                  />
                </form>
              </ModalBody>

              <ModalFooter>
                <Wrap spacing=".5rem">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    colorScheme="gray">
                    {intl.formatMessage({ defaultMessage: "Annuler" })}
                  </Button>

                  <FormStateRHF
                    render={({ isDirty }) => (
                      <Button
                        type="submit"
                        form={formId}
                        isDisabled={!isDirty}
                        isLoading={isLoading}
                        colorScheme="brandSecondary">
                        {intl.formatMessage({ defaultMessage: "Téléverser" })}
                      </Button>
                    )}
                  />
                </Wrap>
              </ModalFooter>
            </FormProvider>
          </ModalContent>
        </Modal>

        {render({
          isAllowed:
            (customerDocument === undefined ||
              (customerDocument.state ===
                CUSTOMERS_DOCUMENTS_STATES_VALUE_PENDING &&
                customerDocument.pages?.length === 0) ||
              customerDocument.state ===
                CUSTOMERS_DOCUMENTS_STATES_VALUE_REFUSED) &&
            (canPerform({
              authorizations:
                AUTHORIZATIONS_AUTHORIZATION_VALUE_CUSTOMERS_DOCUMENTS_CREATE,
            }) ||
              user?.user_type === "customer"),
          trigger: onOpen,
        })}
      </>
    );
  },
);
export default CustomersDocumentsActionCreate;
